import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Tabs, Tab, Button, TextField,
    IconButton, Paper, Container, Alert, Switch, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../api';

const AdminDashboard = () => {
    const [tabValue, setTabValue] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Doctors State
    const [doctors, setDoctors] = useState([]);
    const [newDoctorName, setNewDoctorName] = useState('');
    const [signatureFile, setSignatureFile] = useState(null);

    // Units State
    const [units, setUnits] = useState([]);
    const [newUnitName, setNewUnitName] = useState('');
    const [newUnitAddress, setNewUnitAddress] = useState('');
    const [newUnitLogoFile, setNewUnitLogoFile] = useState(null);

    // Edit Unit State
    const [editUnitOpen, setEditUnitOpen] = useState(false);
    const [currentEditUnit, setCurrentEditUnit] = useState(null);
    const [editUnitName, setEditUnitName] = useState('');
    const [editUnitAddress, setEditUnitAddress] = useState('');
    const [editUnitLogoFile, setEditUnitLogoFile] = useState(null);

    // Edit Doctor State
    const [editDoctorOpen, setEditDoctorOpen] = useState(false);
    const [currentEditDoctor, setCurrentEditDoctor] = useState(null);
    const [editDoctorName, setEditDoctorName] = useState('');
    const [editSignatureFile, setEditSignatureFile] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [unitsRes, doctorsRes] = await Promise.all([
                api.get('/units?include_inactive=true'), // Fetch all units for admin
                api.get('/doctors?include_inactive=true') // Fetch all doctors
            ]);
            setUnits(unitsRes.data);
            setDoctors(doctorsRes.data);
        } catch (error) {
            console.error("Failed to fetch data", error);
        }
    };

    // --- DOCTOR HANDLERS ---

    const handleDoctorSubmit = async (e) => {
        e.preventDefault();
        if (!newDoctorName || !signatureFile) {
            setMessage({ type: 'error', text: 'Name and Signature are required' });
            return;
        }
        const formData = new FormData();
        formData.append('name', newDoctorName);
        formData.append('signature_file', signatureFile);
        try {
            await api.post('/doctors/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setMessage({ type: 'success', text: 'Doctor added successfully' });
            setNewDoctorName('');
            setSignatureFile(null);
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to add doctor' });
        }
    };

    const handleToggleDoctorActive = async (doctor) => {
        try {
            const formData = new FormData();
            formData.append('active', !doctor.active);
            await api.put(`/doctors/${doctor.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to update doctor status' });
        }
    };

    const openEditDoctorDialog = (doctor) => {
        setCurrentEditDoctor(doctor);
        setEditDoctorName(doctor.name);
        setEditSignatureFile(null);
        setEditDoctorOpen(true);
    };

    const handleSaveDoctorEdit = async () => {
        if (!editDoctorName) return;
        try {
            const formData = new FormData();
            formData.append('name', editDoctorName);
            if (editSignatureFile) {
                formData.append('signature_file', editSignatureFile);
            }
            await api.put(`/doctors/${currentEditDoctor.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setEditDoctorOpen(false);
            setMessage({ type: 'success', text: 'Doctor updated successfully' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to update doctor' });
        }
    };


    // --- UNIT HANDLERS ---

    const handleUnitSubmit = async (e) => {
        e.preventDefault();
        if (!newUnitName || !newUnitAddress) {
            setMessage({ type: 'error', text: 'Name and Address are required' });
            return;
        }
        try {
            const res = await api.post('/units/', { name: newUnitName, address: newUnitAddress });
            if (newUnitLogoFile) {
                const formData = new FormData();
                formData.append('logo_file', newUnitLogoFile);
                await api.put(`/units/${res.data.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            setMessage({ type: 'success', text: 'Unit added successfully' });
            setNewUnitName('');
            setNewUnitAddress('');
            setNewUnitLogoFile(null);
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to add unit' });
        }
    };

    const handleToggleUnitActive = async (unit) => {
        try {
            await api.put(`/units/${unit.id}`, { active: !unit.active });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to update unit status' });
        }
    };

    const openEditUnitDialog = (unit) => {
        setCurrentEditUnit(unit);
        setEditUnitName(unit.name);
        setEditUnitAddress(unit.address);
        setEditUnitLogoFile(null);
        setEditUnitOpen(true);
    };

    const handleSaveUnitEdit = async () => {
        if (!editUnitName || !editUnitAddress) return;
        try {
            await api.put(`/units/${currentEditUnit.id}`, { name: editUnitName, address: editUnitAddress });
            if (editUnitLogoFile) {
                const formData = new FormData();
                formData.append('logo_file', editUnitLogoFile);
                await api.put(`/units/${currentEditUnit.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            setEditUnitOpen(false);
            setMessage({ type: 'success', text: 'Unit updated successfully' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to update unit' });
        }
    };

    // --- UI HELPERS ---

    const AdminTable = ({ rows, isDoctor }) => (
        <TableContainer component={Paper} variant="outlined">
            <Table size="medium" sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                        <TableCell sx={{ width: '25%', fontWeight: 'bold' }}>NAME</TableCell>
                        <TableCell sx={{ width: '45%', fontWeight: 'bold' }}>DETAILS</TableCell>
                        <TableCell sx={{ width: '20%', fontWeight: 'bold', textAlign: 'center' }}>STATUS</TableCell>
                        <TableCell sx={{ width: '10%', fontWeight: 'bold', textAlign: 'center' }}>EDIT</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.id}
                            sx={{
                                opacity: row.active ? 1 : 0.6,
                                backgroundColor: row.active ? 'inherit' : '#fafafa'
                            }}
                        >
                            <TableCell sx={{ verticalAlign: 'top' }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {row.name}
                                </Typography>
                                {!row.active && (
                                    <Typography variant="caption" color="error" display="block">
                                        (Disabled)
                                    </Typography>
                                )}
                                {!isDoctor && row.has_logo && row.logo_base64 && (
                                    <Box sx={{ mt: 1 }}>
                                        <img 
                                            src={`data:image/png;base64,${row.logo_base64}`} 
                                            alt="logo thumbnail" 
                                            style={{ maxHeight: 60, maxWidth: 120, objectFit: 'contain' }} 
                                        />
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                                {isDoctor ? (
                                    <Typography variant="body2" color="textSecondary">
                                        {row.signature_path ? "Signature on file" : "No signature"}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {row.address}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={row.active}
                                            onChange={() => isDoctor ? handleToggleDoctorActive(row) : handleToggleUnitActive(row)}
                                            size="small"
                                        />
                                    }
                                    label={row.active ? "Active" : "Inactive"}
                                    labelPlacement="end"
                                    sx={{ mr: 0 }}
                                />
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => isDoctor ? openEditDoctorDialog(row) : openEditUnitDialog(row)}
                                >
                                    <EditIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Admin Dashboard
            </Typography>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} centered>
                    <Tab label="Manage Doctors" />
                    <Tab label="Manage Units" />
                </Tabs>
            </Paper>

            {/* Doctors Tab */}
            {tabValue === 0 && (
                <Box>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Add New Doctor</Typography>
                        <form onSubmit={handleDoctorSubmit}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextField
                                    label="Doctor Name"
                                    value={newDoctorName}
                                    onChange={(e) => setNewDoctorName(e.target.value)}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Upload Sig
                                    <input type="file" hidden accept="image/*" onChange={(e) => setSignatureFile(e.target.files[0])} />
                                </Button>
                                {signatureFile && <Typography variant="caption">{signatureFile.name}</Typography>}
                                <Button type="submit" variant="contained">Add</Button>
                            </Box>
                        </form>
                    </Paper>

                    <AdminTable rows={doctors} isDoctor={true} />

                    {/* Edit Doctor Dialog */}
                    <Dialog open={editDoctorOpen} onClose={() => setEditDoctorOpen(false)}>
                        <DialogTitle>Edit Doctor</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus margin="dense" label="Doctor Name" fullWidth variant="outlined"
                                value={editDoctorName} onChange={(e) => setEditDoctorName(e.target.value)} sx={{ mb: 2 }}
                            />
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}>
                                {editSignatureFile ? "Change Signature (Selected)" : "Upload New Signature (Optional)"}
                                <input type="file" hidden accept="image/*" onChange={(e) => setEditSignatureFile(e.target.files[0])} />
                            </Button>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditDoctorOpen(false)}>Cancel</Button>
                            <Button onClick={handleSaveDoctorEdit} variant="contained">Save</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}

            {/* Units Tab */}
            {tabValue === 1 && (
                <Box>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Add New Unit</Typography>
                        <form onSubmit={handleUnitSubmit}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                <TextField
                                    label="Unit Name" value={newUnitName} onChange={(e) => setNewUnitName(e.target.value)}
                                    size="small" sx={{ width: '200px' }}
                                />
                                <TextField
                                    label="Address" value={newUnitAddress} onChange={(e) => setNewUnitAddress(e.target.value)}
                                    size="small" sx={{ flexGrow: 1 }}
                                />
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                    size="small"
                                >
                                    Upload Logo
                                    <input type="file" hidden accept="image/*" onChange={(e) => setNewUnitLogoFile(e.target.files[0])} />
                                </Button>
                                {newUnitLogoFile && <Typography variant="caption">{newUnitLogoFile.name}</Typography>}
                                <Button type="submit" variant="contained">Add</Button>
                            </Box>
                        </form>
                    </Paper>

                    <AdminTable rows={units} isDoctor={false} />

                    {/* Edit Unit Dialog */}
                    <Dialog open={editUnitOpen} onClose={() => setEditUnitOpen(false)}>
                        <DialogTitle>Edit Unit</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus margin="dense" label="Unit Name" fullWidth variant="outlined"
                                value={editUnitName} onChange={(e) => setEditUnitName(e.target.value)} sx={{ mb: 2 }}
                            />
                            <TextField
                                margin="dense" label="Address" fullWidth variant="outlined" multiline rows={2}
                                value={editUnitAddress} onChange={(e) => setEditUnitAddress(e.target.value)} sx={{ mb: 2 }}
                            />
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}>
                                {editUnitLogoFile ? "Change Logo (Selected)" : "Upload New Logo (Optional)"}
                                <input type="file" hidden accept="image/*" onChange={(e) => setEditUnitLogoFile(e.target.files[0])} />
                            </Button>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditUnitOpen(false)}>Cancel</Button>
                            <Button onClick={handleSaveUnitEdit} variant="contained">Save</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </Container>
    );
};

export default AdminDashboard;
